import {
	createStyles,
	HoverCard,
	Skeleton,
	UnstyledButton,
} from '@mantine/core';
import { useApiGetIamRoleUsers, type IAMRoleOut } from '@repo/api-codegen';
import { Text } from '@repo/foundations';
import { ROW_HEIGHT } from '../TableV2/TableV2.styles';
import { UserAvatarArray } from '../UserAvatar';
import { IamRoleMembersList } from './IamRoleMembersList';

const useStyles = createStyles((theme) => ({
	button: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing.xs,
		flexWrap: 'nowrap',
		padding: `${theme.spacing.xs} 6px`,
		maxHeight: `${ROW_HEIGHT}px`,
		borderRadius: theme.radius.sm,
		minWidth: 140,
		'&:hover': {
			backgroundColor: theme.other.getColor('surface/secondary/default'),
		},
	},
}));

interface IamRoleMembersListButtonProps {
	iamRole: IAMRoleOut;
}

export function IamRoleMembersListButton({
	iamRole,
}: IamRoleMembersListButtonProps) {
	const { cx, classes } = useStyles();

	const { data: users, isLoading } = useApiGetIamRoleUsers(
		{
			pathParams: {
				iamRoleId: iamRole.id,
			},
		},
		{
			select: (result) =>
				result.filter((user) => !user.disabled && !user.is_service_account),
		}
	);

	if (isLoading || !users) {
		return <Skeleton height={32} />;
	}

	const userCount = users?.length || 0;

	return (
		<HoverCard
			width={300}
			shadow="md"
			position="bottom-start"
			withinPortal
			disabled={userCount === 0}
		>
			<HoverCard.Target>
				<UnstyledButton className={cx(classes.button)}>
					{userCount > 0 ? (
						<>
							<UserAvatarArray
								limit={3}
								size="sm"
								spacing="sm"
								disableZIndex={true}
								users={users || []}
							/>
							<Text size="sm" color="text/secondary/default">
								{userCount} member{userCount !== 1 && 's'}
							</Text>
						</>
					) : (
						<Text size="sm" color="text/secondary/default">
							None
						</Text>
					)}
				</UnstyledButton>
			</HoverCard.Target>
			<HoverCard.Dropdown>
				<IamRoleMembersList users={users} />
			</HoverCard.Dropdown>
		</HoverCard>
	);
}
